import type React from 'react';
import {useRef, useState} from 'react';
import {Box, Button, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {Add, Close} from '@mui/icons-material';
import {ClickToEdit} from '~/components/ClickToEdit/ClickToEdit';
import {newId, placeBar} from '../../../domain/planText';
import type {RoadmapBar, ToolKey} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {PICK_COLORS} from '../strategyProps';
import {PlanSection} from '../PlanSection';
import {HelpTitle} from '../SectionLinks';

const ROADMAP_HELP = 'The 3-year roadmap over 12 quarters; the fiscal years follow the fiscal-year setting. It is drafted '
    + 'from PICK: Implement opportunities start in Q1 (3 quarters), Challenge in Q4 (5 quarters), Possible in Q7 '
    + '(4 quarters); Kill ones are left off. Supplier relationship programme and Benefits tracking are always added. '
    + 'Drag a bar to move it, drag either end to change its duration, click a name to rename it, or add and remove '
    + 'activities. Colours follow PICK, and an opportunity that moves to another PICK group moves its bar. The '
    + '"Long-term horizon" check needs a bar that reaches year 3.';

const NAME_COL = 220;
const MAX_BARS = 30;

type Moving = {id: string; start: number; length: number; mode?: 'move' | 'start' | 'end'; fromX?: number;
    fromStart?: number; fromLength?: number};

/** Where a bar lands after dragging by `delta` quarters: the whole bar, or one of its ends (the other end stays). */
function dragged(m: Moving, delta: number): {start: number; length: number} {
    const start = m.fromStart!, length = m.fromLength!;
    if (m.mode === 'end') return placeBar(start, length + delta);
    if (m.mode === 'start') {
        const end = start + length - 1;
        const next = Math.max(1, Math.min(end, start + delta));
        return {start: next, length: end - next + 1};
    }
    return placeBar(start + delta, length);
}

const HANDLE_SX = {position: 'absolute', top: 0, bottom: 0, width: 8, cursor: 'ew-resize', touchAction: 'none',
    '&:hover, &:active': {bgcolor: 'rgba(0,0,0,0.25)'}} as const;

/**
 * Category strategy: a 3-year roadmap over 12 quarters (fiscal years from the settings). Drag a bar along the quarters
 * or drag either end to change its duration; with the keyboard, focus it and use the arrow keys (Shift + arrows changes
 * its length). Bars of opportunities moved to Kill in PICK are hidden; moving an opportunity moves its bar. Colour follows the bar's PICK quadrant; bars
 * drafted from PICK are hidden while PICK is turned off. Bars can be added, renamed and removed.
 */
export const RoadmapSection: React.FC<PlanProps & {openTool: (key: ToolKey) => void}> = ({plan, actions, openTool}) => {
    const {content, derived, reference, canEdit} = plan;
    const grid = useRef<HTMLDivElement>(null);
    const [moving, setMoving] = useState<Moving | null>(null);
    const quadrant = new Map(derived.pick.map(p => [p.key, p.quadrant]));
    const pickOff = derived.toolsOff.includes('pick');
    const bars = content.roadmap.filter(b => !b.pick || (!pickOff && quadrant.get(b.pick) !== 'Kill'));
    // Positions sent but not yet back in `content`, so quick successive key presses build on each other.
    const pending = useRef<Record<string, {start: number; length: number}>>({});
    const positionOf = (bar: RoadmapBar) => {
        const p = pending.current[bar.id];
        if (p && p.start === bar.start && p.length === bar.length) delete pending.current[bar.id];
        return pending.current[bar.id] ?? bar;
    };

    const save = (bars: RoadmapBar[]) => void actions.save('roadmap', bars, 'Roadmap updated.');
    const commit = () => {
        if (!moving) return;
        const bar = content.roadmap.find(b => b.id === moving.id);
        if (bar && (bar.start !== moving.start || bar.length !== moving.length)) {
            pending.current[moving.id] = {start: moving.start, length: moving.length};
            save(content.roadmap.map(b => (b.id === moving.id ? {...b, start: moving.start, length: moving.length} : b)));
        }
        setMoving(null);
    };
    const add = () => void actions.save('roadmap',
        [...content.roadmap, {id: newId('r', content.roadmap), name: 'New activity', start: 1, length: 4, pick: null}],
        'Roadmap activity added.');
    const remove = (bar: RoadmapBar) => void actions.save('roadmap', content.roadmap.filter(b => b.id !== bar.id),
        `${bar.name} removed from the roadmap.`);
    const quarterWidth = () => ((grid.current?.getBoundingClientRect().width ?? 1200) - NAME_COL) / 12;

    return (
        <PlanSection title={<HelpTitle title="Category strategy" help={ROADMAP_HELP}
                                       links={derived.toolsOff.includes('pick') ? [] : [{label: 'PICK', onClick: () => openTool('pick')}]}/>} edited={plan.edited.includes('roadmap')}
                     actions={canEdit && content.roadmap.length < MAX_BARS && (
                         <Button size="small" startIcon={<Add/>} onClick={add}>Add activity</Button>
                     )}>
            <Box ref={grid} sx={{overflowX: 'auto'}}
                 onPointerMove={e => {
                     if (moving?.fromX === undefined) return;
                     const delta = Math.round((e.clientX - moving.fromX) / quarterWidth());
                     setMoving({...moving, ...dragged(moving, delta)});
                 }}
                 onPointerUp={commit}>
                <Box sx={{display: 'grid', gridTemplateColumns: `${NAME_COL}px repeat(12, minmax(48px, 1fr))`, minWidth: 820}}>
                    <span/>
                    {reference.years.map((y, i) => (
                        <Box key={y} sx={{gridColumn: 'span 4', px: 1, pb: 0.5, borderLeft: '1px solid', borderColor: 'divider'}}>
                            <Typography variant="subtitle2">{y}</Typography>
                            <Typography variant="caption" color="text.secondary">{reference.yearThemes[i]}</Typography>
                        </Box>
                    ))}
                    <span/>
                    {Array.from({length: 12}, (_, q) => (
                        <Typography key={q} variant="caption" color="text.secondary"
                                    sx={{px: 1, borderLeft: '1px solid', borderColor: 'divider'}}>{`Q${(q % 4) + 1}`}</Typography>
                    ))}
                    {bars.map((bar, row) => {
                        const shown = moving?.id === bar.id ? {...bar, ...placeBar(moving.start, moving.length)} : bar;
                        const color = bar.pick ? PICK_COLORS[quadrant.get(bar.pick) ?? 'Possible'] : '#7A7F8A';
                        return (
                            <Box key={bar.id} sx={{display: 'contents'}}>
                                <Stack direction="row" alignItems="center" gap={0.25}
                                       sx={{gridRow: row + 3, gridColumn: 1, py: 0.5, pr: 1, minWidth: 0}}>
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <ClickToEdit text={bar.name} readOnly={!canEdit} typographyProps={{variant: 'body2', noWrap: true}}
                                                     onSave={v => v.trim() && v.trim() !== bar.name
                                                         && save(content.roadmap.map(b => (b.id === bar.id ? {...b, name: v.trim()} : b)))}/>
                                    </Box>
                                    {canEdit && (
                                        <Tooltip title="Remove from the roadmap">
                                            <IconButton size="small" aria-label={`Remove ${bar.name}`} onClick={() => remove(bar)}>
                                                <Close fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                                <Box sx={{gridRow: row + 3, gridColumn: `${shown.start + 1} / span ${shown.length}`, py: 0.75, position: 'relative'}}>
                                    <Box
                                        role="slider" tabIndex={canEdit ? 0 : -1}
                                        aria-label={`${bar.name}: quarters ${shown.start} to ${shown.start + shown.length - 1}`}
                                        aria-valuemin={1} aria-valuemax={12} aria-valuenow={shown.start}
                                        onPointerDown={e => {
                                            if (!canEdit) return;
                                            setMoving({id: bar.id, start: bar.start, length: bar.length, mode: 'move',
                                                fromX: e.clientX, fromStart: bar.start, fromLength: bar.length});
                                        }}
                                        onKeyDown={e => {
                                            if (!canEdit || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
                                            e.preventDefault();
                                            const step = e.key === 'ArrowLeft' ? -1 : 1;
                                            const base = moving?.id === bar.id ? moving : positionOf(bar);
                                            setMoving({id: bar.id, ...(e.shiftKey ? placeBar(base.start, base.length + step)
                                                : placeBar(base.start + step, base.length))});
                                        }}
                                        onKeyUp={e => e.key.startsWith('Arrow') && commit()}
                                        onBlur={() => moving?.fromX === undefined && commit()}
                                        sx={{height: 22, borderRadius: 1, bgcolor: color, opacity: 0.85,
                                            cursor: canEdit ? 'grab' : 'default', touchAction: 'none',
                                            '&:focus-visible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1}}}
                                    />
                                    {canEdit && (['start', 'end'] as const).map(end => (
                                        <Box key={end} aria-hidden title={end === 'start' ? 'Drag to change the start' : 'Drag to change the duration'}
                                             onPointerDown={e => {
                                                 e.stopPropagation();
                                                 setMoving({id: bar.id, start: bar.start, length: bar.length, mode: end,
                                                     fromX: e.clientX, fromStart: bar.start, fromLength: bar.length});
                                             }}
                                             sx={{...HANDLE_SX, [end === 'start' ? 'left' : 'right']: 0, my: 0.75,
                                                 borderRadius: end === 'start' ? '4px 0 0 4px' : '0 4px 4px 0'}}/>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            {bars.length === 0 && (
                <Typography variant="body2" color="text.secondary">No activities on the roadmap yet.</Typography>
            )}
        </PlanSection>
    );
};
